/**
 * Day/Night Cycle Engine
 *
 * Drives:
 *   - overworld lighting tint (color + alpha overlay composited by the renderer)
 *   - wild Surggie spawn pool modulation (some species are diurnal/nocturnal/etc.)
 *
 * Design notes:
 *   - In-game time is tracked as a float [0, 24) hours. update(dtMs) advances it.
 *   - One in-game day defaults to 24 real minutes (so 1 real minute = 1 in-game hour).
 *     speedMultiplier scales this — e.g. setSpeed(60) makes one in-game day = 24 real seconds.
 *   - lightingAlpha smoothly interpolates between phase "peaks" so the world doesn't pop.
 *   - No DOM/React/window references. SSR-safe.
 */

export type TimeOfDay = 'dawn' | 'day' | 'dusk' | 'night';

export const PHASE_BOUNDS: Record<TimeOfDay, { startHour: number; endHour: number }> = {
  dawn: { startHour: 5, endHour: 8 },
  day: { startHour: 8, endHour: 18 },
  dusk: { startHour: 18, endHour: 21 },
  night: { startHour: 21, endHour: 5 }, // wraps midnight
};

// Per-phase overlay color + the alpha "peak" used for smooth interpolation.
// The string returned by overlayColor recomputes alpha each frame using lightingAlpha,
// scaled into each phase's natural range.
interface PhaseTint {
  r: number;
  g: number;
  b: number;
  peakAlpha: number;
}

// Tints are drawn from the PIXEL_STYLE.md master palette so the overlay feels
// like the same world, not a Photoshop filter:
//   dawn  → gold #c5a83a (warm sunrise)
//   dusk  → amber midpoint of gold #c5a83a and alert red #c54a4a
//   night → deep #3d6890 (deep ocean-blue, alpha kept low so the world stays readable)
const PHASE_TINTS: Record<TimeOfDay, PhaseTint> = {
  dawn: { r: 197, g: 168, b: 58, peakAlpha: 0.2 },
  day: { r: 255, g: 255, b: 255, peakAlpha: 0 },
  dusk: { r: 197, g: 121, b: 66, peakAlpha: 0.25 },
  night: { r: 61, g: 104, b: 144, peakAlpha: 0.35 },
};

/** Wrap an hour value into [0, 24). */
function wrapHour(h: number): number {
  const m = h % 24;
  return m < 0 ? m + 24 : m;
}

/** Pure helper — returns the phase for a given hour (0..24). */
export function getPhaseForHour(hour: number): TimeOfDay {
  const h = wrapHour(hour);
  if (h >= PHASE_BOUNDS.dawn.startHour && h < PHASE_BOUNDS.dawn.endHour) return 'dawn';
  if (h >= PHASE_BOUNDS.day.startHour && h < PHASE_BOUNDS.day.endHour) return 'day';
  if (h >= PHASE_BOUNDS.dusk.startHour && h < PHASE_BOUNDS.dusk.endHour) return 'dusk';
  return 'night';
}

/**
 * Compute a global 0..1 darkness value where 0 = noon (hour 12) and 1 = midnight (hour 0/24).
 * Cosine curve so phase transitions feel smooth (no snap at phase boundaries).
 */
function darknessForHour(hour: number): number {
  const h = wrapHour(hour);
  // Cosine peaks at midnight (0/24), troughs at noon (12).
  // cos((h/24) * 2π) = 1 at h=0, -1 at h=12, 1 at h=24.
  // Remap to [0,1] with 0 at noon, 1 at midnight.
  const c = Math.cos((h / 24) * Math.PI * 2);
  return (c + 1) / 2;
}

export class DayNightCycle {
  // In-game time, in hours [0, 24).
  private _hour: number;

  // Real ms → in-game ms ratio. At multiplier=1, 1 real minute = 1 in-game hour
  // (so a full in-game day takes 24 real minutes).
  private _speedMultiplier: number;

  // Real ms per in-game hour at multiplier=1. 60_000 ms = 1 minute.
  private static readonly REAL_MS_PER_INGAME_HOUR = 60_000;

  private _phase: TimeOfDay;
  private _handlers: Set<(phase: TimeOfDay) => void>;

  constructor(opts?: { startHour?: number; speedMultiplier?: number }) {
    this._hour = wrapHour(opts?.startHour ?? 12);
    this._speedMultiplier = opts?.speedMultiplier ?? 1;
    this._phase = getPhaseForHour(this._hour);
    this._handlers = new Set();
  }

  get hour(): number {
    return this._hour;
  }

  get phase(): TimeOfDay {
    return this._phase;
  }

  /**
   * 0..1 darkness amount. 0 = noon (bright), 1 = midnight (darkest).
   * Smooth cosine curve so the world fades rather than snaps.
   */
  get lightingAlpha(): number {
    return darknessForHour(this._hour);
  }

  /**
   * rgba() string for the renderer to composite over the world.
   * Alpha is scaled by lightingAlpha (so dawn/dusk blend smoothly toward/from night).
   */
  get overlayColor(): string {
    const tint = PHASE_TINTS[this._phase];
    // For 'day' we always render fully transparent.
    if (this._phase === 'day') {
      return `rgba(${tint.r}, ${tint.g}, ${tint.b}, 0)`;
    }
    // Scale the phase's peak alpha by the global lightingAlpha curve so the overlay
    // breathes with time-of-day rather than snapping to peak across the whole phase.
    // Floor with a small base so the phase is always at least visible while active.
    const breathing = 0.5 + 0.5 * darknessForHour(this._hour);
    const alpha = tint.peakAlpha * breathing;
    return `rgba(${tint.r}, ${tint.g}, ${tint.b}, ${alpha.toFixed(3)})`;
  }

  update(dtMs: number): void {
    if (dtMs <= 0) return;
    const ingameHours =
      (dtMs * this._speedMultiplier) / DayNightCycle.REAL_MS_PER_INGAME_HOUR;
    const next = wrapHour(this._hour + ingameHours);
    this._hour = next;

    const nextPhase = getPhaseForHour(next);
    if (nextPhase !== this._phase) {
      this._phase = nextPhase;
      for (const h of this._handlers) {
        h(nextPhase);
      }
    }
  }

  setSpeed(multiplier: number): void {
    this._speedMultiplier = multiplier;
  }

  setHour(h: number): void {
    this._hour = wrapHour(h);
    const nextPhase = getPhaseForHour(this._hour);
    if (nextPhase !== this._phase) {
      this._phase = nextPhase;
      for (const handler of this._handlers) {
        handler(nextPhase);
      }
    }
  }

  onPhaseChange(handler: (phase: TimeOfDay) => void): () => void {
    this._handlers.add(handler);
    return () => {
      this._handlers.delete(handler);
    };
  }
}

/** Shared singleton — most game systems use this rather than instantiating their own. */
export const dayNightCycle: DayNightCycle = new DayNightCycle();

/**
 * Filter a spawn pool by the current phase.
 * Species without a restriction entry spawn in all phases.
 * Species with a restriction only spawn if `phase` is in their allowed list.
 */
export function filterPoolByPhase<T extends { speciesId: string }>(
  pool: T[],
  phase: TimeOfDay,
  speciesPhaseRestrictions?: Record<string, ReadonlyArray<TimeOfDay>>,
): T[] {
  if (!speciesPhaseRestrictions) return pool.slice();
  return pool.filter((entry) => {
    const restriction = speciesPhaseRestrictions[entry.speciesId];
    if (!restriction) return true; // no restriction = always available
    return restriction.includes(phase);
  });
}
